// CampaignBoard.tsx
// Campaign sending view with CSV upload

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, TestTube2, Send as SendIcon, AlertTriangle } from "lucide-react";
import { PhonePreviewIOS, PhonePreviewAndroid } from "~/components/pages/PhonePreview";
import { asHttpImageOrNull, parseCSV } from "~/components/pages/utils";
import { trpc } from "~/api/trpc";
import { z } from "zod";

// Shared validator so the client matches the server's z.email() rules exactly.
const emailSchema = z.email();
const isValidEmail = (email: string): boolean =>
  emailSchema.safeParse(email.trim()).success;

interface CampaignBoardProps {
  handleSendTestPushnotification: (input: any) => Promise<any>;
  handleSendPushNotificationBatch: (input: any) => Promise<any>;
}

export function CampaignBoard({
  handleSendTestPushnotification,
  handleSendPushNotificationBatch,
}: CampaignBoardProps) {
  // Load templates from backend
  const { data: templatesData } = trpc.pushNotifications.listPushNotificationTemplates.useQuery();
  const templates = templatesData ?? [];
  
  const [templateId, setTemplateId] = useState<string>("");
  const selectedTemplate = templates.find((t) => t.id === templateId);

  const [csvName, setCsvName] = useState<string | null>(null);
  const [contacts, setContacts] = useState<
    Array<{ email: string; mergeVariables: Record<string, string> }>
  >([]);
  const [invalidContacts, setInvalidContacts] = useState<
    Array<{ email: string; row: number }>
  >([]);
  const [showContacts, setShowContacts] = useState(false);
  const [showInvalid, setShowInvalid] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; total: number } | null>(null);
  const [testEmail, setTestEmail] = useState("");

  // Helper function to extract placeholders from template (handles new fields)
  const extractPlaceholdersFromTemplateFields = (template: any): string[] => {
    const placeholderSet = new Set<string>();
    const regex = /\{\{([^}]+)\}\}/g;
    
    // Check all relevant fields
    const fieldsToCheck = [
      template.androidTitle,
      template.androidBody,
      template.iosTitle,
      template.iosSubtitle,
      template.iosBody,
      // Fallback to old fields if present
      template.title,
      template.subtitle,
      template.body,
    ];
    
    fieldsToCheck.forEach((field) => {
      if (typeof field === 'string') {
        let match;
        while ((match = regex.exec(field)) !== null) {
          placeholderSet.add(match[1]);
        }
      }
    });
    
    return Array.from(placeholderSet);
  };

  const handleUploadCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCsvName(f.name);
    setInvalidContacts([]);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const { rows } = parseCSV(text);

        const hasEmail = rows.some((r) => r.email || r.Email || r.EMAIL);
        if (!hasEmail) {
          alert("CSV must include a column named 'email'.");
          setContacts([]);
          setInvalidContacts([]);
          return;
        }

        const placeholders = selectedTemplate
          ? extractPlaceholdersFromTemplateFields(selectedTemplate)
          : [];

        const valid: Array<{
          email: string;
          mergeVariables: Record<string, string>;
        }> = [];
        const invalid: Array<{ email: string; row: number }> = [];

        rows.forEach((r, index) => {
          const email = r.email || r.Email || r.EMAIL || "";
          if (!email) return;
          if (!isValidEmail(email)) {
            // +2 accounts for the header row plus 1-based indexing.
            invalid.push({ email, row: index + 2 });
            return;
          }
          const mv: Record<string, string> = {};
          placeholders.forEach((p) => {
            mv[p] = r[p] ?? "";
          });
          valid.push({ email, mergeVariables: mv });
        });

        setContacts(valid);
        setInvalidContacts(invalid);
      } catch {
        alert("Could not parse CSV.");
        setContacts([]);
        setInvalidContacts([]);
      }
    };
    reader.readAsText(f);
  };

  const imageFromTemplateUrl = asHttpImageOrNull(
    (selectedTemplate?.data as any)?.image
  );
  const previewImage = imageFromTemplateUrl
    ? { name: "template-image", dataUrl: imageFromTemplateUrl }
    : null;

  const handleTest = async () => {
    if (!selectedTemplate) return alert("Select a template first.");
    if (!testEmail) return alert("Enter a test email.");
    try {
      const input = {
        email: testEmail,
        notification: {
          // Platform-specific fields - the router will handle splitting these
          androidTitle: selectedTemplate.androidTitle,
          androidBody: selectedTemplate.androidBody,
          iosTitle: selectedTemplate.iosTitle,
          iosSubtitle: selectedTemplate.iosSubtitle ?? undefined,
          iosBody: selectedTemplate.iosBody,
          richContent: imageFromTemplateUrl
            ? { image: imageFromTemplateUrl }
            : undefined,
          mutableContent: !!imageFromTemplateUrl,
          data: { templateId: selectedTemplate.id },
        },
        campaignId: `campaign_test_${selectedTemplate.id}_${Date.now()}`,
        mergeVariables: {},
      } as const;
      await handleSendTestPushnotification(input);
      alert("Test sent.");
    } catch (e: any) {
      alert(e?.message || "Test failed");
    }
  };

  const handleSendCampaign = async () => {
    if (!selectedTemplate) return alert("Select a template.");
    if (contacts.length === 0) return alert("Upload a CSV with emails first.");
    try {
      setProgress({ sent: 0, total: contacts.length });
      const res = await handleSendPushNotificationBatch({
        recipients: contacts,
        templateId: selectedTemplate.id,
        campaignId: `cmp_${Date.now()}`,
        onProgress: (current: number, total: number) =>
          setProgress({ sent: current, total }),
      });
      alert(res.message);
      setProgress(null);
    } catch (e: any) {
      alert(e?.message || "Failed to send campaign.");
      setProgress(null);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:auto-rows-min">
      <div className="lg:col-span-7 space-y-4">
        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <SendIcon className="h-4 w-4" /> Send Campaign
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-1 block">Template</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Choose a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">
                        No templates available
                      </div>
                    ) : (
                      templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block">
                  Upload CSV (must include <code>email</code>)
                </Label>
                <label className="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm w-fit">
                  <Upload className="h-4 w-4" />
                  <span>Choose file</span>
                  <input
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleUploadCSV}
                  />
                </label>
                <div className="mt-1 text-xs text-muted-foreground">
                  {csvName ?? "No file selected"}
                </div>
              </div>
            </div>

            {invalidContacts.length > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    {invalidContacts.length}{" "}
                    {invalidContacts.length === 1 ? "row" : "rows"} skipped due to
                    invalid email.
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowInvalid(true)}
                  className="rounded-xl"
                >
                  Show skipped
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-56 rounded-xl"
              />
              <Button variant="outline" onClick={handleTest} className="rounded-xl">
                <TestTube2 className="mr-2 h-4 w-4" /> Test
              </Button>
            </div>

            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={contacts.length === 0}
                onClick={() => setShowContacts(true)}
                className="rounded-xl"
              >
                Show contacts ({contacts.length})
              </Button>
            </div>

            <div className="pt-2">
              <Button
                onClick={handleSendCampaign}
                disabled={!templateId || contacts.length === 0}
                className="rounded-xl"
              >
                <SendIcon className="mr-2 h-4 w-4" /> Send Campaign
              </Button>
              {progress && (
                <div className="mt-2 text-xs text-muted-foreground">
                  Sending… {progress.sent}/{progress.total}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-5 lg:row-span-2 self-start">
        <Card className="rounded-2xl h-full">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selectedTemplate ? selectedTemplate.name : "Preview"}
            </CardTitle>
          </CardHeader>
          <CardContent className="py-4">
            {selectedTemplate ? (
              <div className="space-y-4">
                <div className="text-xs text-muted-foreground text-center">Live Previews</div>
                
                {/* iOS Preview */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-blue-600 text-center">iOS</div>
                  <div className="flex justify-center">
                    <PhonePreviewIOS
                      appName="Your App"
                      header={selectedTemplate.iosTitle}
                      subtitle={selectedTemplate.iosSubtitle ?? undefined}
                      body={selectedTemplate.iosBody}
                      link={undefined}
                      image={previewImage}
                    />
                  </div>
                </div>

                {/* Android Preview */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-green-600 text-center">Android</div>
                  <div className="flex justify-center">
                    <PhonePreviewAndroid
                      appName="Your App"
                      title={selectedTemplate.androidTitle}
                      body={selectedTemplate.androidBody}
                      image={previewImage}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center">
                Select a template to see preview.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showContacts} onOpenChange={setShowContacts}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>CSV Contacts ({contacts.length})</DialogTitle>
          </DialogHeader>
          <div className="rounded border p-3 text-sm space-y-2">
            {contacts.length === 0 ? (
              <div className="text-muted-foreground">No contacts loaded.</div>
            ) : (
              <ul className="space-y-2">
                {contacts.map((c, i) => (
                  <li key={i} className="rounded-lg border p-2">
                    <div className="font-medium">{c.email}</div>
                    {Object.keys(c.mergeVariables).length > 0 ? (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {Object.entries(c.mergeVariables).map(([k, v]) => (
                          <div key={k}>
                            <span className="font-mono">{`{{${k}}}`}</span>:{" "}
                            {v || <span className="text-rose-600">(empty)</span>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-amber-600">
                        No merge variables found for this contact.
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowContacts(false)}
              className="rounded-xl"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showInvalid} onOpenChange={setShowInvalid}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Skipped Rows ({invalidContacts.length})</DialogTitle>
          </DialogHeader>
          <div className="rounded border p-3 text-sm space-y-2">
            {invalidContacts.length === 0 ? (
              <div className="text-muted-foreground">No skipped rows.</div>
            ) : (
              <ul className="space-y-2">
                {invalidContacts.map((c, i) => (
                  <li key={i} className="rounded-lg border p-2">
                    <div className="font-medium">
                      {c.email || <span className="text-rose-600">(empty)</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Row {c.row} - invalid email format
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInvalid(false)}
              className="rounded-xl"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}